"use client";
import { useState, useMemo } from 'react';
import Image from "next/image";
import Link from "next/link";
import styles from "./projects.module.css";

import { useTranslations, useLocale } from 'next-intl';

export default function ProjectsGrid({ initialProjects }) {
    const locale = useLocale();
    const t = useTranslations('Filters');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [sortOrder, setSortOrder] = useState('newest'); // 'newest' or 'oldest' feature

    const itemsPerPage = 6;

    // Helper for localization
    const getLocalized = (project, key) => {
        if (project[key + '_i18n'] && project[key + '_i18n'][locale]) {
            return project[key + '_i18n'][locale];
        }
        return project[key];
    };

    // derived categories
    const categories = useMemo(() => {
        const cats = new Set(initialProjects.map(p => getLocalized(p, 'category')).filter(Boolean));
        // We need to map these to objects if we want to translate 'All', but here 'All' is a special key.
        // Let's keep it simple for now, but handle 'All' translation in UI.
        return ['All', ...Array.from(cats)];
    }, [initialProjects, locale]);

    // Filter and Sort Logic
    const filteredProjects = useMemo(() => {
        let projects = [...initialProjects];

        // Category Filter
        if (selectedCategory !== 'All') {
            projects = projects.filter(p => getLocalized(p, 'category') === selectedCategory);
        }

        // Search
        if (searchTerm) {
            const lowerTerm = searchTerm.toLowerCase();
            projects = projects.filter(p => {
                const title = getLocalized(p, 'title').toLowerCase();
                const location = (getLocalized(p, 'location') || '').toLowerCase();
                return title.includes(lowerTerm) || location.includes(lowerTerm);
            });
        }

        // Sort
        projects.sort((a, b) => {
            // Priority: Completion Year > Created At
            const dateA = a.completion_year ? new Date(a.completion_year, 0, 1) : new Date(a.created_at || 0);
            const dateB = b.completion_year ? new Date(b.completion_year, 0, 1) : new Date(b.created_at || 0);
            return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
        });

        return projects;
    }, [initialProjects, searchTerm, selectedCategory, sortOrder, locale]);

    // Pagination logic
    const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);
    const paginatedProjects = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredProjects.slice(start, start + itemsPerPage);
    }, [currentPage, filteredProjects, itemsPerPage]);

    // Reset pagination
    useMemo(() => setCurrentPage(1), [searchTerm, selectedCategory, sortOrder]);

    const handleCategoryChange = (cat) => {
        // If cat is an object, use its name/id, otherwise use raw string
        setSelectedCategory(typeof cat === 'object' ? cat.name : cat);
    };

    return (
        <div>
            {/* Controls */}
            <div className={styles.controls}>
                <div className={styles.filterGroup}>
                    {categories.map((cat) => (
                        <button
                            key={cat}
                            onClick={() => handleCategoryChange(cat)}
                            className={`${styles.filterButton} ${selectedCategory === cat ? styles.activeFilter : ''}`}
                        >
                            {cat === 'All' ? t('all') : cat}
                        </button>
                    ))}
                </div>

                <div className={styles.searchGroup}>
                    <input
                        type="text"
                        placeholder={t('searchProjects')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={styles.searchInput}
                    />
                    <select
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value)}
                        className={styles.sortSelect}
                    >
                        <option value="newest">{t('newestFirst')}</option>
                        <option value="oldest">{t('oldestFirst')}</option>
                    </select>
                </div>
            </div>

            {/* Grid */}
            <div className={styles.uniformGrid}>
                {paginatedProjects.length > 0 ? (
                    paginatedProjects.map((project, index) => (
                        <Link href={`/${locale}/projects/${project.id || index}`} key={project.id || index} className={styles.card}>
                            <div className={styles.imageWrapper}>
                                <Image
                                    src={project.image}
                                    alt={getLocalized(project, 'title')}
                                    fill
                                    className={styles.image}
                                />
                            </div>
                            <div className={styles.content}>
                                <span className={styles.category}>{getLocalized(project, 'category')}</span>
                                <h3 className={styles.title}>{getLocalized(project, 'title')}</h3>
                                {project.location && <span className={styles.location}>📍 {getLocalized(project, 'location')}</span>}

                                <div className={styles.meta}>
                                    <span>{getLocalized(project, 'status') || 'Completed'}</span>
                                    {project.completion_year && <span> • {project.completion_year}</span>}
                                </div>
                                <span className={styles.readMore}>{t('viewProject')} &rarr;</span>
                            </div>
                        </Link>
                    ))
                ) : (
                    <div className={styles.noResults}>
                        <p>{t('noProjects')}</p>
                        <button onClick={() => { setSearchTerm(''); setSelectedCategory('All'); }} className={styles.clearButton}>
                            {t('clearFilters')}
                        </button>
                    </div>
                )}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className={styles.pagination}>
                    <button
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        className={styles.pageButton}
                    >
                        &lt; {t('prev')}
                    </button>
                    <span>{t('page')} {currentPage} of {totalPages}</span>
                    <button
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        className={styles.pageButton}
                    >
                        {t('next')} &gt;
                    </button>
                </div>
            )}
        </div>
    );
}
